import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Bugsnag from "@bugsnag/js";
import { crop, scale } from "@cloudinary/url-gen/actions/resize";
import { auto } from "@cloudinary/url-gen/qualifiers/quality";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import ArrowPathIcon from "@heroicons/react/24/outline/ArrowPathIcon";
import CameraIcon from "@heroicons/react/24/outline/CameraIcon";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import ViewfinderCircleIcon from "@heroicons/react/24/outline/ViewfinderCircleIcon";
import { isCancel } from "axios";
import { useRouter } from "next/router";
import { useDropzone } from "react-dropzone";
import {
  RegisterOptions,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";

import ProgressButton from "components/button/progressButton/ProgressButton";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import Upsell from "components/form/upsell/Upsell";
import PhotoEditModal, {
  PhotoEdits,
} from "components/modal/photoEditModal/PhotoEditModal";
import { useSubtotal } from "hooks/useSubtotal";
import { useDeletePhoto, useUploadPhoto } from "queries/legacy/photo-queries";
import { useGetFlags, useGetProduct } from "queries/legacy/product-queries";
import { useGetCustomisation } from "queries/legacy/product-queries";
import { UploadedPhoto, UploadPhotosSchemaField } from "types/schema";
import { cloudinary } from "utils/cloudinary-utils";
import {
  sanitizeCustomisation,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import styles from "./UploadPhotos.module.scss";
import PhotoActionButton from "./photoActionButton/PhotoActionButton";

/**
 * Create a context for UploadPhoto to pass shared data.
 *
 * Saves a bunch of prop drilling and simplifies future changes.
 */
type UploadPhotosContextState = {
  aspectRatio: string;
  name: string;
  photo?: UploadedPhoto;
};
const UploadPhotosContext = createContext<UploadPhotosContextState | undefined>(
  undefined
);

export function useUploadPhotosContext() {
  const context = useContext(UploadPhotosContext);
  if (context === undefined) {
    throw new Error("useUploadPhotoContext must be used within UploadPhoto");
  }
  return context;
}

type UploadPhotosProps = {
  aspectRatio: string;
  name: string;
  onPhotoDeleted?: () => void;
  onPhotoUploaded?: (photo: UploadedPhoto) => void;
  registerOptions: RegisterOptions;
  upsells?: UploadPhotosSchemaField["metas"][0]["upsells"];
};

/**
 * Component for uploading, cropping, and filtering user photos.
 *
 * Uncontrolled due to the passing of photos.
 *
 * Use in RHF via `Controller` and this component's `onChange` event.
 */
export default function UploadPhotos({
  aspectRatio,
  name,
  onPhotoDeleted,
  onPhotoUploaded,
  registerOptions,
  upsells,
}: UploadPhotosProps) {
  const router = useRouter();

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const {
    field: { onChange },
  } = useController({ control, name, rules: registerOptions });

  const value = useWatch({ name });

  const { data: product } = useGetProduct();
  const { data: flags } = useGetFlags();
  const { getSubtotalPropertyPrice, updateSubtotal } = useSubtotal(
    product?.museSlug
  );

  const interpolate = useInterpolate();
  const { data: customisation } = useGetCustomisation();

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPhotoEditModalOpen, setIsPhotoEditModalOpen] = useState(false);

  const [photo, setPhoto] = useState<UploadedPhoto | undefined>(value);

  const contextValue = useMemo(
    () => ({ aspectRatio, photo, name }),
    [aspectRatio, photo, name]
  );

  const updatePhotoSubtotal = useCallback(() => {
    const currentPhotoPrice = getSubtotalPropertyPrice(
      "dedication_photo_upload"
    );
    const upsellPrice = upsells?.find((s) => s.id === "true")?.price.amount;

    if (currentPhotoPrice > 0) {
      return;
    }

    if (upsellPrice) {
      updateSubtotal({
        dedication_photo_upload: Number(Number(upsellPrice).toFixed(2)),
      });
    }
  }, [getSubtotalPropertyPrice, updateSubtotal, upsells]);

  const deletePhoto = useDeletePhoto({});
  const uploadPhoto = useUploadPhoto(name, {
    onSuccess: (newPhoto) => {
      updatePhotoSubtotal();
      setPhoto(newPhoto);
      onPhotoUploaded?.(newPhoto);
    },
    onError: (error) => {
      if (error.name === "rejected") {
        Bugsnag.notify(
          new Error("Photo rejected by content moderation"),
          (event) => {
            event.severity = "info";
            event.unhandled = false;
            event.addMetadata("Query", router.query);
            event.addMetadata(
              "Customisation",
              customisation ? sanitizeCustomisation(customisation) : {}
            );
            // Add the moderation reasons to the report
            event.addMetadata("Moderation", {
              reasons: error.reasons,
            });
          }
        );
      } else if (!isCancel(error)) {
        // ^ Don't notify Bugsnag if the user cancelled the upload manually
        Bugsnag.notify(
          error instanceof Error
            ? error
            : new Error("Unhandled error uploading photo"),
          (event) => {
            event.severity = "info";
            event.unhandled = false;
            event.addMetadata("Query", router.query);
            event.addMetadata(
              "Customisation",
              customisation ? sanitizeCustomisation(customisation) : {}
            );
          }
        );
      }
    },
  });

  const pendingFileRef = useRef<File | null>(null);
  const uploadPhotoAbortSignalRef = useRef<AbortController | null>(null);

  useEffect(() => {
    onChange(photo ?? "");
  }, [photo, onChange]);

  const onUploadPhoto = useCallback(
    (file: File) => {
      if (!product) {
        throw SSR_CONFIGURATION_ERROR;
      }

      setUploadProgress(0);

      // We pass this signal to the upload mutation so we can cancel it if needed.
      uploadPhotoAbortSignalRef.current = new AbortController();

      // Sync mutation – see `useUploadPhoto.onSuccess` for what happens next
      uploadPhoto.mutate({
        aspectRatio,
        file,
        preset:
          flags?.imageModeration?.status === "enabled"
            ? "moderated"
            : "ml_default",
        signal: uploadPhotoAbortSignalRef.current.signal,
        museSlug: product.museSlug,
        onUploadProgress: (event) =>
          setUploadProgress(Math.round((event.progress || 0) * 100)),
      });
    },
    [product, uploadPhoto, aspectRatio, flags?.imageModeration?.status]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        // Store a reference to this file for retries if needed.
        pendingFileRef.current = acceptedFiles[0];
        onUploadPhoto(acceptedFiles[0]);
      }
    },
    [onUploadPhoto]
  );

  const {
    getInputProps,
    getRootProps,
    open: openDropzone,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".png", ".gif", ".jpeg", ".jpg", ".heic"],
    },
    multiple: false,
    noClick: true,
  });

  // Don't allow any changes to UploadPhotos fields when editing an order
  const isDisabled = !!router.query.order_id;

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  function onDismissPhotoEditModal() {
    setIsPhotoEditModalOpen(false);
  }

  function handleDeletePhoto() {
    if (photo) {
      try {
        deletePhoto.mutate({ photoId: photo.public_id });
      } catch (error) {
        const loggableError =
          error instanceof Error
            ? error
            : new Error("Failed to delete uploaded photo");

        Bugsnag.notify(loggableError, (event) => {
          event.severity = "info";
          event.unhandled = false;
          event.addMetadata("Query", router.query);
          event.addMetadata(
            "Customisation",
            customisation ? sanitizeCustomisation(customisation) : {}
          );
        });
      }
    }

    setPhoto(undefined);
    uploadPhoto.reset();
  }

  function onDeletePhoto() {
    handleDeletePhoto();
    onPhotoDeleted?.();
  }

  function onTryAnother() {
    handleDeletePhoto();
    openDropzone();
  }

  function onRetryPhotoUpload() {
    if (pendingFileRef.current) {
      onUploadPhoto(pendingFileRef.current);
    }
  }

  function onCancelPhotoUpload() {
    uploadPhotoAbortSignalRef.current?.abort();
    uploadPhoto.reset();
  }

  function onPhotoEditComplete(edits: PhotoEdits) {
    setIsPhotoEditModalOpen(false);

    const newPhoto = { ...photo!, ...edits };

    setPhoto(newPhoto);
    onPhotoUploaded?.(newPhoto);
  }

  function onEditPhoto() {
    setIsPhotoEditModalOpen(true);
  }

  return (
    <ErrorBoundary>
      <UploadPhotosContext.Provider value={contextValue}>
        <div
          className={[
            styles.slot,
            !photo && styles.emptySlot,
            !!errors?.[name] && styles.errorSlot,
            isDisabled ? styles.disabledSlot : "",
          ].join(" ")}
        >
          {upsells && (
            <Upsell
              amount={
                // special case for photo upsells, their SKU id is true/false to indicate presence
                // of a photo as the field value is the data rather than a predictable string
                upsells.find((s) => s.id === "true")?.price.formatted || "0.00"
              }
            />
          )}

          {photo && (
            <div className={styles.uploadedPhoto}>
              <img
                alt=""
                className={styles.editedImage}
                src={cloudinary()
                  .image(photo.public_id)
                  .resize(
                    crop()
                      .width(photo.crop.width)
                      .height(photo.crop.height)
                      .x(photo.crop.x)
                      .y(photo.crop.y)
                  )
                  .resize(scale().width(320))
                  .format(auto())
                  .toURL()}
              />
              {!isDisabled && (
                <div className={styles.photoActions}>
                  <PhotoActionButton
                    Icon={ViewfinderCircleIcon}
                    data-testid="picture-crop"
                    onClick={onEditPhoto}
                  >
                    {interpolate(
                      product.content.canvas.form_upload_photo_crop_label ||
                        CANVAS_FALLBACK_CONTENT.form_upload_photo_crop_label
                    )}
                  </PhotoActionButton>
                  <PhotoActionButton
                    Icon={TrashIcon}
                    data-testid="picture-remove"
                    onClick={onDeletePhoto}
                  >
                    {interpolate(
                      product.content.canvas.form_upload_photo_delete_label ||
                        CANVAS_FALLBACK_CONTENT.form_upload_photo_delete_label
                    )}
                  </PhotoActionButton>
                </div>
              )}
            </div>
          )}

          {!photo && (
            <div
              className={[
                styles.empty,
                uploadPhoto.isError && styles.emptyError,
              ].join(" ")}
            >
              {(uploadPhoto.isIdle || uploadPhoto.isSuccess) && (
                <label
                  {...getRootProps({
                    className: styles.uploadLabel,
                  })}
                >
                  <CameraIcon className={styles.uploadIcon} />

                  <p className={styles.emptyText}>
                    {interpolate(
                      product.content.canvas.form_upload_photo_empty_title ||
                        CANVAS_FALLBACK_CONTENT.form_upload_photo_empty_title
                    )}
                    <br />
                    <small>
                      {interpolate(
                        product.content.canvas
                          .form_upload_photo_empty_subtitle ||
                          CANVAS_FALLBACK_CONTENT.form_upload_photo_empty_subtitle
                      )}
                    </small>
                  </p>

                  <input
                    data-testid={`upload_photo_${name}`}
                    disabled={isDisabled}
                    {...getInputProps({ className: styles.uploadInput })}
                  />
                </label>
              )}

              {uploadPhoto.isLoading && (
                <ProgressButton
                  onClick={onCancelPhotoUpload}
                  progress={uploadProgress}
                >
                  <XMarkIcon className={styles.progressCancelIcon} />
                </ProgressButton>
              )}

              {uploadPhoto.isError && (
                <>
                  {uploadPhoto.error.name === "rejected" && (
                    <>
                      <p>
                        {interpolate(
                          product.content.canvas
                            .form_upload_photo_rejected_message ||
                            CANVAS_FALLBACK_CONTENT.form_upload_photo_rejected_message
                        )}
                      </p>

                      <div className={styles.photoActions}>
                        <PhotoActionButton
                          Icon={CameraIcon}
                          onClick={onTryAnother}
                        >
                          {interpolate(
                            product.content.canvas
                              .form_upload_photo_try_another_label ||
                              CANVAS_FALLBACK_CONTENT.form_upload_photo_try_another_label
                          )}
                        </PhotoActionButton>
                      </div>
                    </>
                  )}

                  {uploadPhoto.error.name !== "rejected" && (
                    <>
                      <p>
                        {interpolate(
                          product.content.canvas
                            .form_upload_photo_error_message ||
                            CANVAS_FALLBACK_CONTENT.form_upload_photo_error_message
                        )}
                      </p>

                      <div className={styles.photoActions}>
                        <PhotoActionButton
                          Icon={ArrowPathIcon}
                          onClick={onRetryPhotoUpload}
                        >
                          {interpolate(
                            product.content.canvas
                              .form_upload_photo_retry_label ||
                              CANVAS_FALLBACK_CONTENT.form_upload_photo_retry_label
                          )}
                        </PhotoActionButton>
                        <PhotoActionButton
                          Icon={TrashIcon}
                          onClick={onDeletePhoto}
                        >
                          {interpolate(
                            product.content.canvas
                              .form_upload_photo_delete_label ||
                              CANVAS_FALLBACK_CONTENT.form_upload_photo_delete_label
                          )}
                        </PhotoActionButton>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {photo && (
          <PhotoEditModal
            isOpen={isPhotoEditModalOpen}
            onDismiss={onDismissPhotoEditModal}
            onEditComplete={onPhotoEditComplete}
            title="Edit photo"
          />
        )}

        {isDisabled && (
          <p className={styles.warning}>
            {interpolate(
              product.content.canvas.form_field_disabled_message ||
                CANVAS_FALLBACK_CONTENT.form_field_disabled_message
            )}
          </p>
        )}
      </UploadPhotosContext.Provider>
    </ErrorBoundary>
  );
}
